import React, { useEffect, useRef, useState, useCallback } from 'react';
import Badge from '../../ui/Badge';
import {
  formatPrice,
  formatDateOnly,
  relativeTime,
  getCardActions,
  getStatusMeta,
  displayStatus,
  getRoleTurn,
  isClosedByListingDelete,
  isOfferClosed,
  offerClosedStatusText,
  PRIMARY_ACTION_LABELS,
  TERMINAL_OFFER_STATUSES,
} from '../utils';
import {
  financingLoanAmount,
  getContingencyDescription,
  getContingencyLabelWithDeadline,
  getCureElectionSummary,
  getFinancingTermsSummary,
  getLeadPaintElectionSummary,
  getSummaryContingencyKeys,
  getTitlePeriodsSummary,
} from '../contingencyCatalog';
import { formatDeadline, isDeadlinePast, getExpiryUrgency } from '../offerExpiration';
import DepositVerificationAgent from '../../../agents/depositVerificationAgent';
import './OfferCard.css';

/**
 * Escrow-receipt state for the Badge `escrow` variant. Earnest money goes into
 * escrow after acceptance, so only an accepted offer has one: 'received' once
 * the buyer uploads the escrow receipt, 'needed' until then, and null (no
 * badge) for any other status. A closed offer keeps a receipt it already has,
 * so both parties can still open it. Older proof-of-funds uploads don't count.
 */
export function normalizeVerification(offer) {
  const dv = offer?.deposit_verification;
  const hasReceipt = !!dv?.has_document && dv.document_type === 'escrow_receipt';
  if (offer?.status === 'accepted') return hasReceipt ? 'received' : 'needed';
  if (offer?.status === 'closed' && hasReceipt) return 'received';
  return null;
}

const SECONDARY_LABELS = {
  ...PRIMARY_ACTION_LABELS,
  accept: 'Accept offer',
  counter: 'Submit counter',
  reject: 'Reject offer',
  withdraw: 'Withdraw offer',
  view: 'View in workspace',
  history: 'Offer history',
  viewHouse: 'View house',
  verifyDeposit: 'Upload escrow receipt',
};

function formatDelta(offerPrice, listPrice) {
  const op = Number(offerPrice);
  const lp = Number(listPrice);
  if (!isFinite(op) || !isFinite(lp) || lp <= 0) return null;
  const pct = ((op - lp) / lp) * 100;
  const rounded = Math.round(pct * 10) / 10;
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  const abs = Math.abs(rounded).toFixed(1);
  return { pct: rounded, label: `${sign}${abs}% vs asking` };
}

/**
 * Whether the offer price favors the viewer's side. A below-asking price is
 * favorable to the buyer (they pay less); an above-asking price is favorable to
 * the seller (they get more). An at-asking price is neutral for both. Drives the
 * green "good" tone on the price delta so each party reads strength at a glance.
 */
export function isFavorableDelta(delta, role) {
  if (!delta || delta.pct === 0) return false;
  if (role === 'seller') return delta.pct > 0;
  return delta.pct < 0;
}

/**
 * Plain-language strength read for the seller's offer list: a weak color signal
 * alone isn't accessible, so sellers get an explicit "Above / At / Below asking"
 * word next to the delta. Returns null when there's no list price to compare.
 */
export function sellerStrengthLabel(delta) {
  if (!delta) return null;
  if (delta.pct > 0) return 'Above asking';
  if (delta.pct < 0) return 'Below asking';
  return 'At asking';
}

export default function OfferCard({
  offer,
  currentRole = 'buyer',
  onAction,
  onViewHouse,
  onViewHistory,
  index = 0,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuTriggerRef = useRef(null);

  const meta = getStatusMeta(displayStatus(offer));
  const turn = getRoleTurn(offer, currentRole);
  const verification = normalizeVerification(offer);
  // The API returns contingencies as a flag dict; legacy rows may hold an array
  // of keys. getSelectedContingencyKeys normalizes both.
  const activeContingencies = getSummaryContingencyKeys(offer.contingencies);
  // The buyer's lead-based paint election (WI pre-1978 / unknown-year homes)
  // gets its own line; its contingency isn't listed twice.
  const leadPaintSummary = getLeadPaintElectionSummary(offer.contingencies);
  // The seller's cure right under 7(d), when the offer records one (WI).
  const cureSummary = getCureElectionSummary(offer.contingencies);
  // The financing contingency's loan terms and the title periods (WI, QA N13).
  const financingSummary = getFinancingTermsSummary(
    offer.contingencies, financingLoanAmount(offer.offer_price, offer.down_payment_percent),
  );
  const titleSummary = getTitlePeriodsSummary(offer.contingencies);

  // Only actions the viewer can take right now (the workspace's turn rule):
  // a buyer waiting on the seller isn't offered accept or counter.
  const { primary: primaryActionKey, menu: nextActions } = getCardActions(offer, currentRole);

  // A closed offer is read-only (legal copy §15.3): no negotiation actions, and
  // no "View house" once the listing was deleted. It stays viewable in the
  // workspace and its history, with the documents the buyer signed.
  const closed = isOfferClosed(offer);
  const listingDeleted = isClosedByListingDelete(offer);
  const closedText = offerClosedStatusText(offer);
  // A buyer whose offer was declined because the seller accepted another offer on
  // the same home: the reason itself lives in the workspace timeline, but
  // property_under_contract marks it on the row (another offer holds the live
  // accepted deal), so surface the "why" on the card instead of a bare "rejected".
  const declinedForAnotherOffer =
    currentRole === 'buyer'
    && displayStatus(offer) === 'rejected'
    && offer.property_under_contract === true;
  const overflowItems = closed
    ? [
      { key: 'view', label: SECONDARY_LABELS.view },
      { key: 'history', label: SECONDARY_LABELS.history },
      ...(listingDeleted ? [] : [{ key: 'viewHouse', label: SECONDARY_LABELS.viewHouse }]),
    ]
    : [
      ...nextActions.map((k) => ({ key: k, label: SECONDARY_LABELS[k] || k })),
      { key: 'viewHouse', label: SECONDARY_LABELS.viewHouse },
      { key: 'history',   label: SECONDARY_LABELS.history },
    ];

  const fireAction = useCallback(
    (key) => {
      setMenuOpen(false);
      if (key === 'viewHouse' && typeof onViewHouse === 'function') {
        onViewHouse(offer);
        return;
      }
      if (key === 'history' && typeof onViewHistory === 'function') {
        onViewHistory(offer);
        return;
      }
      if (typeof onAction === 'function') onAction(key, offer);
    },
    [onAction, onViewHouse, onViewHistory, offer]
  );

  // Menu keyboard contract (role="menu"): move focus into the menu on open,
  // rove with Up/Down, Escape closes and returns focus to the trigger; an
  // outside click dismisses it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const items = () => Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
    const first = items()[0];
    if (first) first.focus();
    const onDocClick = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (menuTriggerRef.current && menuTriggerRef.current.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        if (menuTriggerRef.current) menuTriggerRef.current.focus();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const list = items();
        if (!list.length) return;
        e.preventDefault();
        const idx = list.indexOf(document.activeElement);
        const nextIdx = e.key === 'ArrowDown'
          ? (idx < 0 ? 0 : (idx + 1) % list.length)
          : (idx <= 0 ? list.length - 1 : idx - 1);
        list[nextIdx].focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const delta = formatDelta(offer.offer_price, offer.list_price);
  // An offer that's out of the running (rejected / withdrawn / expired / closed)
  // shouldn't read as strong — a declined bid glowing green "above asking"
  // misleads a seller scanning the field. The live offers and the accepted
  // winner keep the favorable tone and the strength word.
  const outOfRunning = TERMINAL_OFFER_STATUSES.includes(displayStatus(offer));
  const deltaToneClass = isFavorableDelta(delta, currentRole) && !outOfRunning
    ? 'offer-card__delta--good'
    : 'offer-card__delta--neutral';
  // Sellers get an explicit above/at/below-asking read; buyers already have the
  // signed percentage, so the word would just repeat it for them. It's dropped
  // once an offer is out of the running (the comparison no longer matters).
  const strengthLabel =
    currentRole === 'seller' && !outOfRunning ? sellerStrengthLabel(delta) : null;

  // Only a live offer can still lapse; terminal statuses already say why it ended.
  const showExpiry = !!offer.expires_at && !meta?.isTerminal;
  const expiryPast = showExpiry && isDeadlinePast(offer.expires_at);
  // Escalating cue as the deadline nears, so a live offer doesn't lapse unnoticed.
  const expiry = showExpiry && !expiryPast
    ? getExpiryUrgency(offer.expires_at)
    : { level: 'none', relative: null };

  const showVerifyLink = !closed && currentRole === 'buyer' && verification === 'needed';

  // Get first image from property_image_urls if available, otherwise try legacy fields
  const thumb = offer.property_image || offer.property_thumbnail || null;
  const propertyImages = offer.property_image_urls && Array.isArray(offer.property_image_urls) && offer.property_image_urls.length > 0
    ? offer.property_image_urls[0]
    : null;

  return (
    <article
      className={`offer-card offer-card--status-${displayStatus(offer)}`}
      style={{ '--card-index': index }}
    >
      {/* Section 0 — Property image (new) */}
      {propertyImages ? (
        <div className="offer-card__image-container">
          <img
            className="offer-card__image"
            src={propertyImages}
            alt={offer.property_address || 'Property image'}
            width={400}
            height={250}
          />
        </div>
      ) : null}

      {/* Section 1 — Header strip */}
      <header className="offer-card__header">
        <div className="offer-card__header-left">
          <Badge variant="status" value={displayStatus(offer)} pulse={offer.status === 'pending'} />
          <Badge variant="turn" value={turn} />
        </div>
        <div className="offer-card__header-right">
          {verification ? <Badge variant="escrow" value={verification} /> : null}
          {showVerifyLink ? (
            <button
              type="button"
              className="offer-card__verify-link"
              onClick={() => fireAction('verifyDeposit')}
            >
              Upload receipt
            </button>
          ) : null}
          {verification === 'received' ? (
            <a
              className="offer-card__verify-link"
              href={DepositVerificationAgent.getDownloadUrl(offer.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              View receipt
            </a>
          ) : null}
        </div>
      </header>

      {/* Section 2 — Property line */}
      <div className="offer-card__property">
        {thumb ? (
          <img
            className="offer-card__thumb"
            src={thumb}
            alt={offer.property_address || ''}
            width={40}
            height={40}
          />
        ) : null}
        <h3 className="offer-card__address">
          {offer.property_address || `Property #${offer.property_id || offer.id}`}
        </h3>
      </div>

      {/* Section 3 — Price hero */}
      <div className="offer-card__price-hero">
        <p className="offer-card__price">{formatPrice(offer.offer_price)}</p>
        {delta ? (
          <span className={`offer-card__delta ${deltaToneClass}`}>{delta.label}</span>
        ) : null}
        {strengthLabel ? (
          <span
            className={`offer-card__strength ${
              isFavorableDelta(delta, 'seller')
                ? 'offer-card__strength--strong'
                : 'offer-card__strength--neutral'
            }`}
            data-testid={`offer-strength-${offer.id}`}
          >
            {strengthLabel}
          </span>
        ) : null}
      </div>

      {/* Section 4 — KV summary */}
      <dl className="offer-card__kv">
        <div className="offer-card__kv-item">
          <dt className="offer-card__kv-label">Down payment</dt>
          <dd className="offer-card__kv-value">{offer.down_payment_percent ?? '—'}%</dd>
        </div>
        <div className="offer-card__kv-item">
          <dt className="offer-card__kv-label">Closing date</dt>
          <dd className="offer-card__kv-value">
            {formatDateOnly(offer.closing_date)}
          </dd>
        </div>
        <div className="offer-card__kv-item">
          <dt className="offer-card__kv-label">Earnest</dt>
          <dd className="offer-card__kv-value">
            {offer.earnest_money != null ? formatPrice(offer.earnest_money) : '—'}
          </dd>
        </div>
      </dl>

      {closedText ? (
        <p className="offer-card__closed-note" role="note" data-testid={`offer-closed-note-${offer.id}`}>
          {closedText}
        </p>
      ) : null}

      {declinedForAnotherOffer ? (
        <p className="offer-card__decline-note" role="note" data-testid={`offer-decline-note-${offer.id}`}>
          Another offer on this property was accepted.
        </p>
      ) : null}

      {showExpiry ? (
        <p
          className={
            'offer-card__expiry' +
            (expiry.level === 'urgent' ? ' offer-card__expiry--urgent'
              : expiry.level === 'soon' ? ' offer-card__expiry--soon' : '')
          }
          data-testid={`offer-expiry-${offer.id}`}
        >
          {/* The expiry job runs on a schedule, so a lapsed offer can still
              read "pending" for a while; the deadline itself is the truth. */}
          {expiryPast ? (
            'Expired on '
          ) : expiry.relative ? (
            <>
              <strong className="offer-card__expiry-rel">
                {expiry.level === 'urgent' ? '⏳ ' : ''}Expires {expiry.relative}
              </strong>
              {' · '}
            </>
          ) : (
            'Expires '
          )}
          <time dateTime={offer.expires_at}>
            {formatDeadline(offer.expires_at, offer.property_timezone)}
          </time>{' '}
          (property&apos;s local time)
        </p>
      ) : null}

      {/* Section 5 — Collapsible details */}
      <details className="offer-card__details">
        <summary className="offer-card__details-summary">
          <span>More details</span>
          <span className="offer-card__chevron" aria-hidden="true">▾</span>
        </summary>
        <div className="offer-card__details-body">
          {activeContingencies.length > 0 || leadPaintSummary || cureSummary ? (
            <div className="offer-card__details-section">
              <h4 className="offer-card__details-heading">Contingencies</h4>
              <ul className="offer-card__contingencies">
                {activeContingencies.map((c) => (
                  <li key={c} className="offer-card__contingency">
                    <span className="offer-card__contingency-mark" aria-hidden="true">✓</span>
                    <span>
                      <strong>{getContingencyLabelWithDeadline(c, offer.contingencies)}</strong>
                      {getContingencyDescription(c) ? (
                        <> — {getContingencyDescription(c)}</>
                      ) : null}
                    </span>
                  </li>
                ))}
                {leadPaintSummary && (
                  <li className="offer-card__contingency" data-testid="offer-card-lead-paint">
                    <span className="offer-card__contingency-mark" aria-hidden="true">✓</span>
                    <span><strong>{leadPaintSummary}</strong></span>
                  </li>
                )}
                {cureSummary && (
                  <li className="offer-card__contingency" data-testid="offer-card-cure">
                    <span className="offer-card__contingency-mark" aria-hidden="true">✓</span>
                    <span><strong>{cureSummary}</strong></span>
                  </li>
                )}
                {financingSummary && (
                  <li className="offer-card__contingency" data-testid="offer-card-financing-terms">
                    <span className="offer-card__contingency-mark" aria-hidden="true">✓</span>
                    <span><strong>{financingSummary}</strong></span>
                  </li>
                )}
              </ul>
            </div>
          ) : (
            <div className="offer-card__details-section">
              <h4 className="offer-card__details-heading">Contingencies</h4>
              <p className="offer-card__details-empty">None</p>
            </div>
          )}
          {titleSummary ? (
            <div className="offer-card__details-section">
              <h4 className="offer-card__details-heading">Title</h4>
              <p className="offer-card__details-text" data-testid="offer-card-title-periods">{titleSummary}</p>
            </div>
          ) : null}
          {offer.additional_terms ? (
            <div className="offer-card__details-section">
              <h4 className="offer-card__details-heading">Additional terms</h4>
              <p className="offer-card__details-text">{offer.additional_terms}</p>
            </div>
          ) : null}
          <p className="offer-card__updated">
            Last updated {relativeTime(offer.updated_at || offer.created_at)}
          </p>
        </div>
      </details>

      {/* Section 6 — Action bar */}
      <footer className="offer-card__actions">
        {primaryActionKey ? (
          <button
            type="button"
            className="offer-card__primary"
            onClick={() => fireAction(primaryActionKey)}
          >
            {PRIMARY_ACTION_LABELS[primaryActionKey] || primaryActionKey}
          </button>
        ) : (
          <span className="offer-card__terminal-label" data-testid={`offer-terminal-label-${offer.id}`}>
            {meta?.label || 'Closed'}
          </span>
        )}

        <div className="offer-card__overflow-wrap">
          <button
            type="button"
            ref={menuTriggerRef}
            className="offer-card__overflow"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="More actions"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span aria-hidden="true">⋯</span>
          </button>
          {menuOpen ? (
            <div
              ref={menuRef}
              role="menu"
              className="offer-card__menu"
            >
              {overflowItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className="offer-card__menu-item"
                  onClick={() => fireAction(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </footer>
    </article>
  );
}
